import { useMemo } from 'react';
import { StanDataRow } from '@/components';
import type { DataRowActionButton, StanDataColumn } from '@/components/core-components/StanDataRow/StanDataRow.types';
import { formatCurrency } from '@/lib/currency';
import { cn } from '@/lib';
import { useUser } from '@/queries/useUser';
import type { FunnelListItem } from '../../types';
import { FunnelActionsMenu } from './FunnelActionsMenu';
import { getAnalyticsValue, getFunnelPageIcon } from '../../logic';
import { getFunnelsListGridTemplate } from '../../constants/funnelsListColumns';

interface FunnelRowProps {
    funnel: FunnelListItem;
    onEdit: (funnel: FunnelListItem) => void;
    onDuplicate: (funnel: FunnelListItem) => void;
    onDelete: (funnel: FunnelListItem) => void;
    isNewlyDuplicated?: boolean;
    expanded?: boolean;
    onClick?: (e?: React.MouseEvent) => void;
    className?: string;
}

/**
 * A single funnel row using StanDataRow. Designed to be used as the child
 * of a StanAccordionHeader with `asChild`, which injects an onClick handler
 * that toggles the accordion.
 */
export const FunnelRow = ({
    funnel,
    onEdit,
    onDuplicate,
    onDelete,
    isNewlyDuplicated = false,
    expanded = false,
    onClick,
    className,
}: FunnelRowProps) => {
    const { user } = useUser();
    const currency = user?.data?.currency || 'USD';
    const gridTemplate = getFunnelsListGridTemplate();

    const columns: StanDataColumn[] = useMemo(() => {
        const sales = getAnalyticsValue(funnel.analytics, 'sales');
        const leads = getAnalyticsValue(funnel.analytics, 'leads');
        const views = getAnalyticsValue(funnel.analytics, 'views');

        return [
            {
                id: 'name',
                name: 'Name',
                icon: getFunnelPageIcon(funnel.firstPageType),
                value: <span className='text-body-2-bold text-dark truncate'>{funnel.name}</span>,
            },
            {
                id: 'sales',
                name: 'Sales',
                value: <span className='text-body-md text-dark font-bold'>{formatCurrency(sales, currency)}</span>,
                className: 'hidden md:block',
            },
            {
                id: 'pages',
                name: 'Pages',
                value: <span className='text-body-md text-dark'>{funnel.pageCount}</span>,
                className: 'hidden md:block',
            },
            {
                id: 'leads',
                name: 'Leads',
                value: <span className='text-body-md text-dark'>{leads}</span>,
                className: 'hidden md:block',
            },
            {
                id: 'views',
                name: 'Views',
                value: <span className='text-body-md text-dark'>{views}</span>,
                className: 'hidden md:block',
            },
            {
                id: 'url',
                name: 'URL',
                type: 'link',
                value: funnel.noHttpsUrl,
                href: funnel.url,
                showCopyButton: true,
                className: 'hidden md:block',
            },
        ];
    }, [funnel, currency]);

    const actionButtons: DataRowActionButton[] = useMemo(
        () => [
            {
                id: `funnel-actions-${funnel.store_id}`,
                type: 'dropdown' as const,
                label: 'Funnel Actions',
                menu: (
                    <FunnelActionsMenu funnel={funnel} onEdit={onEdit} onDuplicate={onDuplicate} onDelete={onDelete} />
                ),
            },
        ],
        [funnel, onEdit, onDuplicate, onDelete]
    );

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.();
        }
    };

    return (
        <div
            id={`funnel-card-${funnel.store_id}`}
            role='button'
            tabIndex={0}
            onKeyDown={handleKeyDown}
            className={cn(
                'focus-visible:ring-brand-selected p-0! transition-all duration-500 ease-out focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset md:p-3!',
                expanded ? 'rounded-t-lg' : 'rounded-lg',
                isNewlyDuplicated && 'animate-bounce-in',
                className
            )}
        >
            <StanDataRow
                columns={columns}
                gridTemplate={gridTemplate}
                actionButtons={actionButtons}
                variant='simple'
                clickable
                onClick={onClick}
                className='cursor-pointer'
            />
        </div>
    );
};
